import Title from '@cohort/components-xps/components/Title';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import TriggerCta from '@cohort/wallet/apps/TriggerCta';
import {getAppFromTriggerIntegration} from '@cohort/wallet/apps/TriggerIntegration';
import BaseModal from '@cohort/wallet/components/modals/BaseModal';
import DrawerModal from '@cohort/wallet/components/modals/DrawerModal';
import DetailsComponentWrapper from '@cohort/wallet/components/space/DetailsComponentWrapper';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {JourneyStepTriggerWDto} from '@cohort/wallet-schemas/journeySteps';
import React from 'react';
import {useTranslation} from 'react-i18next';

type TriggerPickerModalProps = {
  journeyId: string;
  journeyParticipationId: string;
  journeyStepId: string;
  triggers: Array<JourneyStepTriggerWDto>;
  selectedTrigger: JourneyStepTriggerWDto | null;
  setSelectedTrigger: (trigger: JourneyStepTriggerWDto) => void;
  buildRedirectUrl: (trigger: JourneyStepTriggerWDto) => string;
  onClose: () => void;
};

type TriggerButtonProps = {
  journeyId: string;
  journeyParticipationId: string;
  journeyStepId: string;
  trigger: JourneyStepTriggerWDto;
  isSelected: boolean;
  onTriggerSelected: () => void;
};

const TriggerButton: React.FC<TriggerButtonProps> = ({
  journeyId,
  journeyParticipationId,
  journeyStepId,
  trigger,
  isSelected,
  onTriggerSelected,
}) => {
  const {hasDarkBg, accentColor} = useThemeContext();
  const app = getAppFromTriggerIntegration(trigger.triggerIntegrationId);

  return (
    <li
      className={classNames(
        'flex cursor-pointer items-center justify-between gap-2 rounded-lg border-2 border-transparent px-5 py-3',
        hasDarkBg
          ? 'bg-white/30 text-black/80 text-white'
          : 'bg-black/4 text-black/40 text-gray-900',
        isSelected && '!bg-transparent'
      )}
      style={{
        borderColor: isSelected ? accentColor : undefined,
      }}
      onClick={onTriggerSelected}
    >
      <div className="flex flex-col gap-4 ">
        <div className="flex-grow text-lg font-semibold">{trigger.title}</div>
        <DetailsComponentWrapper
          journeyId={journeyId}
          journeyParticipationId={journeyParticipationId}
          journeyStepId={journeyStepId}
          trigger={trigger}
        />
      </div>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {app.spec.userConnector !== null && app.logo !== null && (
        <div className="flex w-20 flex-col items-center space-y-2">
          {React.cloneElement(app.logo, {height: 32, width: 32})}
          <p className="text-center text-sm font-medium">{app.spec.name}</p>
        </div>
      )}
    </li>
  );
};

const TriggerPickerModal: React.FC<TriggerPickerModalProps> = ({
  journeyId,
  journeyParticipationId,
  journeyStepId,
  triggers,
  selectedTrigger,
  setSelectedTrigger,
  buildRedirectUrl,
  onClose,
}) => {
  const {t} = useTranslation('apps', {keyPrefix: 'triggerPickerModal'});
  const {isMobile} = useScreenSizeContext();

  const ModalComponent = isMobile ? DrawerModal : BaseModal;

  return (
    <ModalComponent
      title={<Title content={t('title')} classes="text-2xl font-semibold" />}
      subtitle={<Subtitle content={t('subtitle')} classes="!text-base font-medium" />}
      tracking={{
        namespace: 'modals.journeys.triggers.picker',
        metadata: {
          journeyId,
          journeyParticipationId,
          journeyStepId,
        },
      }}
      onClose={onClose}
    >
      <ul className="mt-4 flex w-full list-none flex-col space-y-4">
        {triggers.map(trigger => (
          <TriggerButton
            key={trigger.id}
            journeyId={journeyId}
            journeyParticipationId={journeyParticipationId}
            journeyStepId={journeyStepId}
            trigger={trigger}
            isSelected={trigger.id === selectedTrigger?.id}
            onTriggerSelected={() => setSelectedTrigger(trigger)}
          />
        ))}
      </ul>
      {selectedTrigger && (
        <div className="mt-8 text-right">
          <TriggerCta
            journeyId={journeyId}
            journeyParticipationId={journeyParticipationId}
            journeyStepId={journeyStepId}
            trigger={selectedTrigger}
            xpsRedirectUrl={buildRedirectUrl(selectedTrigger)}
          />
        </div>
      )}
    </ModalComponent>
  );
};

export default TriggerPickerModal;
